import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/TablesForBrigades.css';
import Table from '../Table/Table';
import Modal from '../Modal/Modal';
import Brigade from '../Brigades/Brigade';
// Кастомные хуки
import { useRole } from '../../customHook/useRole';
import { useSocketListener } from '../../customHook/useSocketListener';
// Утилиты
import { defaultCellRender } from '../../utils/formatValueForHTML';
import { convertDateToStringWithTime } from '../../utils/convertToDate';
// Действия Redux
import { emitGetCoordsStart, emitGetCoordsStop, listenBrigadesCoords } from '../../redux/actions/brigades.action';
import { useSocketEmitOnUnmount } from '../../customHook/useSocketEmitOnUnmount';
import CopyButton from '../CustomElements/CopyButton';
import { copyCoordsToClipboard } from '../../utils/copyCoordsToClipboard';

function TablesForBrigades({ formData, topBoundaryRef, bottomBoundaryRef, t }) {
  const dispatch = useDispatch();
  // Получение данных о бригадах из Redux store
  const { brigades } = useSelector((state) => state.brigadesState);
  const [currentBrigade, setCurrentBrigade] = useState();

  // Состояния для отображения компонента и модального окна
  const [isOpen, setIsOpen] = useState(false);
  const [openBrigadeModal, setOpenBrigadeModal] = useState(false);

  // Открыть модалку для просмотра бригады
  const openBrigade = (id) => {
    setCurrentBrigade(brigades?.find((brigade) => brigade.id === id));
    setOpenBrigadeModal(true);
    setIsOpen(true);
  };

  // Закрыть модальное окно
  const closeModal = () => {
    setIsOpen(false);
    setOpenBrigadeModal(false);
  };

  // Фильтрация данных на основе роли пользователя
  const isAdmin = useRole('admin');
  const isMaster = useRole('master');
  const isDispatcher = useRole('dispatcher');
  const isEngineerInfosignal = useRole('engineerInfosignal');

  const columns = useMemo(
    () =>
      [
        {
          Header: t?.tableHeaderID || 'ID ИУР',
          accessor: 'id',
          Cell: ({ cell: { value } }) => (
            <div className="table-for-cell-id">
              <span className="cell-id-icon"></span>
              <span className="cell-id-value">{value}</span>
            </div>
          ),
        },
        (isAdmin || isEngineerInfosignal || isMaster || isDispatcher) && {
          Header: t?.tableHeaderStart || 'Начало',
          accessor: 'start',
          Cell: ({ value }) => (
            <span className="date-cell ">{defaultCellRender(convertDateToStringWithTime(value))}</span>
          ),
        },
        (isAdmin || isEngineerInfosignal || isMaster || isDispatcher) && {
          Header: t?.tableHeaderFinish || 'Конец',
          accessor: 'finish',
          Cell: ({ value }) => (
            <span className="date-cell ">{defaultCellRender(convertDateToStringWithTime(value))}</span>
          ),
        },
        {
          Header: t?.tableHeaderStatus,
          accessor: 'status',
          disableSorting: true, // Флаг для отключения сортировки
          tooltipText:
            t?.tooltipStatus || 'Сортировка по статусу не активна, Вы можете выполнить фильтрацию по этому полю.', // Текст для тултипа
          Cell: ({ cell: { value } }) => {
            if (typeof value === 'boolean') {
              return (
                <div className={`icon__item ${value ? 'status-working' : 'status-not-working'}`}>
                  <span className="icon__text">{value ? t?.statusWorking : t?.statusNotWorking}</span>
                </div>
              );
            }

            let statusClass = '';
            let statusText = '';

            switch (value) {
              case 0:
                statusClass = 'status-working';
                statusText = t?.statusWorking;
                break;
              case 1:
                statusClass = 'status-not-working';
                statusText = t?.statusNotWorking;
                break;
              case 2:
                statusClass = 'status-orange';
                statusText = t?.statusOrange;
                break;
              case 3:
                statusClass = 'status-blue';
                statusText = t?.statusBlue;
                break;
              default:
                statusClass = 'status-blue';
                statusText = t?.statusBlue;
                break;
            }

            return (
              <div className={`icon__item ${statusClass}`}>
                <span className="icon__text">{statusText}</span>
              </div>
            );
          },
        },

        // {
        //   Header: t?.tableHeaderResponsible,
        //   accessor: 'responsible',
        //   Cell: ({ value }) => <span className="text-nowrap number-cell">{defaultCellRender(value)}</span>,
        // },
        {
          Header: t?.tableHeaderRoad,
          accessor: 'road',
          Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
        },
        {
          Header: t?.tableHeaderDirectorate,
          accessor: 'directorate',
          Cell: ({ value }) => defaultCellRender(value),
        },
        {
          Header: t?.tableHeaderDepartment,
          accessor: 'department',
          Cell: ({ value }) => <span className="text-break">{defaultCellRender(value)}</span>,
        },
        {
          Header: t?.tableHeaderStation,
          accessor: 'station',
          width: 300,
          Cell: ({ value }) => (
            <span className="text-break" style={{ minWidth: '200px' }}>
              {defaultCellRender(value)}
            </span>
          ),
        },
        {
          Header: t?.tableHeaderObject,
          accessor: 'obj',
          Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
        },
        {
          Header: t?.tableHeaderRail,
          accessor: 'rail',
          Cell: ({ value }) => <span className="text-nowrap">{defaultCellRender(value)}</span>,
        },
        {
          Header: t?.copyCoords,
          accessor: 'copyCoords',
          disableSorting: true,
          Cell: ({ row: { original } }) => <CopyButton onCopy={() => copyCoordsToClipboard(original)} />,
        },
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brigades, t]
  );

  // старт прослушивания сокета
  useSocketListener([listenBrigadesCoords]);
  //прекращение прослушивания координат
  useSocketEmitOnUnmount([emitGetCoordsStop]);

  useEffect(() => {
    //отправляем событие чтобы сервер начал слать маркеры
    dispatch(emitGetCoordsStart({ isBounds: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table-for-brigades_container">
      {brigades && (
        <Table
          data={brigades ? brigades : []}
          columns={columns}
          open={openBrigade}
          showGlobalSearch={false}
          topBoundaryRef={topBoundaryRef}
          bottomBoundaryRef={bottomBoundaryRef}
          arrLength={brigades.length}
          disableInternalSorting
        />
      )}
      {/* Модальное окно */}
      {isOpen && (
        <Modal isOpen={isOpen} onClose={closeModal} containerSelector={'.brigades'} minusSize="--sadr-font-size-40">
          {/* Компонент для просмотра устройства */}
          {openBrigadeModal && (
            <Brigade
              closeModal={closeModal}
              currentBrigade={currentBrigade}
              setCurrentBrigade={setCurrentBrigade}
              formData={formData}
              t={t}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

export default TablesForBrigades;
